import { reactMount } from '@/lib/reactMount'
import { fetcher } from '@/lib/fetcher'
import { Fragment, useState, useMemo, useEffect } from 'react'
import useSWRMutation from 'swr/mutation'
import { format } from 'date-fns'

import { Button } from '@/components/Button'
import { Tag } from '@/components/Tag'
import { Delete, ArrowRight, Chevron } from '@/components/Icons'

export const SearchPage = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(12)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchedTerm, setSearchedTerm] = useState('')
  const [searchResults, setSearchResults] = useState(null)

  const {
    error,
    isMutating,
    trigger: performSearch
  } = useSWRMutation('/api/Search/Search', fetcher)

  const posts = searchResults?.data || []

  const filteredPosts = useMemo(() => {
    if (!searchTerm.trim()) return posts

    const term = searchTerm.toLowerCase()
    return posts
  }, [searchTerm, posts])

  const totalItems = filteredPosts.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems)
  const currentPosts = filteredPosts.slice(startIndex, endIndex)

  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push('...')
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(totalPages)
      }
    }

    return pages
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleSearch = async (e) => {
    e.preventDefault()
    if (searchTerm.trim()) {
      setSearchedTerm(searchTerm)
      setCurrentPage(1)
      const result = await performSearch({
        method: 'GET',
        params: {
          q: encodeURIComponent(searchTerm)
        }
      })
      setSearchResults(result)
    }
  }

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value)
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value)
  }

  const handleClearSearch = () => {
    setSearchTerm('')
    setSearchedTerm('')
    setCurrentPage(1)
    setSearchResults(null)
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const q = params.get('q')
    if (q && !searchTerm) {
      setSearchTerm(q)
      setSearchedTerm(q)
      performSearch({
        method: 'GET',
        params: { q: encodeURIComponent(q) }
      }).then(result => setSearchResults(result))
    }
  }, [])

  return (
    <>
      <div className='flex flex-col gap-16 max-w-7xl w-full mx-auto px-4 sm:px-6 md:px-8 pb-20 pt-40'>

        {/* Header */}
        <div className='flex flex-col gap-5 w-full max-w-[908px]'>

          <h1 className='font-extrabold text-[--base-text] text-3xl md:text-4xl mb-1.5'>Search Results</h1>
          <form onSubmit={handleSearch} className='flex flex-col sm:flex-row gap-3 items-center w-full'>
            <div className='flex flex-col w-full sm:w-[542px]'>
              <div className='relative w-full'>
                <input
                  id='searchTerm'
                  name='searchTerm'
                  type='text'
                  placeholder='Search posts...'
                  aria-label='Search'
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                  disabled={isMutating}
                  className='bg-default-50 h-11 w-full rounded-md border border-default-200 px-4 pr-10 pb-1 font-semibold text-default-700 text-base focus:outline-none shadow-sm'
                />
                {searchTerm && (
                  <button className='absolute right-1 top-1 outline-none inline-flex items-center justify-center size-[36px] rounded-full hover:bg-primary-100 active:bg-primary-100' type='button' onClick={handleClearSearch}><span className='sr-only'>Clear</span>
                    <Delete className='size-4' />
                  </button>
                )}
              </div>
            </div>
            <Button
              variant='secondary'
              size='xl'
              type='submit'
              disabled={isMutating}
              className='max-sm:hidden'
            >
              {isMutating ? 'Searching...' : 'Search'}
            </Button>
          </form>
          <div>
            {error && (
              <p className='font-semibold text-[--base-text] text-base w-full'>Error searching posts. Please try again.</p>
            )}
            {!error && (
              <p className='font-semibold text-[--base-text] text-base w-full'>
                {totalItems} search result{totalItems !== 1 ? 's' : ''}
                {searchedTerm && <> for <span className='font-extrabold'>"{searchedTerm}"</span></>}
              </p>
            )}
          </div>

        </div>
        {/* Results list */}
        <div className='flex flex-col gap-12 w-full max-w-[908px]'>
          <div className='flex flex-col gap-8 w-full'>

            {isMutating && (
              <div className='text-center py-12'>
                <div className='inline-flex items-center'>
                  <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-3' />
                  <p className='text-gray-500 text-lg'>Searching posts...</p>
                </div>
              </div>
            )}

            {!isMutating && currentPosts.length > 0 && (
              currentPosts.map((post) => (
                <Fragment key={post.Date}>
                  <a href={post.Button.Url} target={post.Button.Target} className='flex flex-col gap-3 pb-5 w-full border-b border-default-300'>
                    <div className='flex flex-row gap-4 items-center'>
                      {post.PageType.map((type, index) => (
                        <Tag key={index}>{type}</Tag>
                      ))}
                    </div>
                    <div>
                      <h3 className='font-extrabold text-primary-950 text-base w-full mb-1.5'>
                        {post.Title}
                      </h3>
                      {post.Description && <p className='font-semibold text-default-600 text-sm w-full'>{post.Description}</p>}
                    </div>
                  </a>
                </Fragment>
              ))
            )}
            {!isMutating && currentPosts.length === 0 && (
              // No posts found message but it shows 0 results at top of page (not sure if we will still need this?)
              <div className='text-left py-12'>
                <p className='text-lg font-semibold'>No posts found matching your search criteria.</p>
              </div>
            )}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <nav
              aria-label='Pagination'
              className='w-full'
            >
              <div className='hidden lg:flex flex-row items-center justify-between w-full'>
                <p className='text-default-500 font-semibold text-sm max-lg:grow text-center shrink-0'>
                  Page {currentPage} of{' '}
                  {totalPages}
                </p>

                <div className='flex flex-row gap-1 grow justify-end'>

                  {/* Previous button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center gap-1 pl-4 pr-5 pt-[7px] pb-[9px] rounded border font-extrabold text-sm shadow-sm transition duration-300 ${currentPage === 1
                          ? 'text-default-300 border-default-300 pointer-events-none'
                          : 'border-default-900 text-default-900'
                        }`}
                  >
                    <ArrowRight className='rotate-180 size-6' />
                    <span>Previous</span>
                  </button>

                  {/* Page numbers */}
                  {getPageNumbers().map((page, index) => (
                    <Fragment key={index}>
                      {page === '...' && (
                        <span className='flex justify-center items-center pb-2.5 bg-default-50 border border-default-200 text-default-900 text-center font-extrabold text-sm rounded px-1 py-2 w-11 shadow-sm'>
                          ...
                        </span>
                      )}
                      {page !== '...' && (
                        <button
                          onClick={() => handlePageChange(page)}
                          className={`flex justify-center items-center pb-2.5 font-extrabold text-sm rounded px-1 py-2 w-11 shadow-sm hover:bg-default-900 hover:text-default-50 transition duration-300 ${page === currentPage
                                ? 'z-10 text-default-50 bg-primary-950 border border-primary-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-950'
                                : 'bg-default-50 border border-default-200 text-default-900'
                              }`}
                        >
                          {page}
                        </button>
                      )}
                    </Fragment>
                  ))}

                  {/* Next button */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`flex justify-center items-center gap-1 pl-5 max-lg:pr-5 pr-4 pt-[7px] pb-2 lg:pb-[9px] rounded border border-default-900 text-default-900 font-extrabold text-sm shadow-sm hover:bg-default-900 hover:text-default-50 transition duration-300 ${currentPage === totalPages
                          ? 'text-default-300 border-default-300 pointer-events-none'
                          : 'border-default-900 text-default-900'
                        }`}
                  >
                    <span>Next</span>
                    <ArrowRight className='size-6' />
                  </button>

                </div>
                {/* Items per page selector */}
                {posts.length >= 12 && (
                  <label className='relative ml-12'>
                    <select
                      id='itemsPerPage'
                      name='itemsPerPage'
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      className='cursor-pointer bg-default-50 h-11 w-full rounded-lg border border-default-200 focus:border-default-400 pb-px pr-10 pl-3 font-semibold text-[14px] text-default-700 appearance-none shadow-sm'
                    >
                      <option value={12}>12 / page</option>
                      <option value={24}>24 / page</option>
                      <option value={48}>48 / page</option>
                    </select>
                    <Chevron className='text-default-700 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 size-5' />
                  </label>
                )}
              </div>

              {/* Mobile pagination */}
              <div className='flex flex-row items-center justify-between w-full lg:hidden'>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`flex justify-center items-center gap-1 pl-5 max-lg:pr-5 pr-4 pt-[7px] pb-2 lg:pb-[9px] rounded border font-extrabold text-sm shadow-sm hover:bg-default-900 hover:text-default-50 transition duration-300 ${currentPage === 1
                      ? 'text-default-300 border-default-300 pointer-events-none'
                      : 'border-default-900 text-default-900'
                    }`}
                >
                  <span className='sr-only'>previous</span>
                  <ArrowRight className='rotate-180 size-6' />
                </button>
                <p className='text-default-500 font-semibold text-sm grow text-center'>
                  Page {currentPage} of{' '}
                  {totalPages}
                </p>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`flex justify-center items-center gap-1 pl-5 max-lg:pr-5 pr-4 pt-[7px] pb-2 lg:pb-[9px] rounded border border-default-900 text-default-900 font-extrabold text-sm shadow-sm hover:bg-default-900 hover:text-default-50 transition duration-300 ${currentPage === totalPages
                      ? 'text-default-300 border-default-300 pointer-events-none'
                      : 'border-default-900 text-default-900'
                    }`}
                >
                  <span className='sr-only'>Next</span>
                  <ArrowRight className='size-6' />
                </button>
              </div>
            </nav>
          )}
        </div>
      </div>
    </>
  )
}

reactMount('SearchPage', SearchPage)
